import { useState } from "react";
import { AnimatePresence, motion, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId, scrollToTop } from "../../lib/scroll";

const LINKS = [
    { id: "o-nas", label: "O nas" },
    { id: "uslugi", label: "Usługi" },
    { id: "realizacje", label: "Realizacje" },
    { id: "opinie", label: "Opinie" },
    { id: "proces", label: "Proces" },
    { id: "kontakt", label: "Kontakt" },
];

export function Logo({ dark = false }) {
    return (
        <button
            data-testid="logo-btn"
            onClick={scrollToTop}
            className="flex items-baseline gap-1.5"
            aria-label="Handy's — do góry strony"
        >
            <span className={`font-display text-2xl font-black tracking-tighter ${dark ? "text-white" : "text-ink"}`}>
                Handy&rsquo;s
            </span>
            <span className="h-2 w-2 rounded-full bg-gold" />
        </button>
    );
}

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { scrollY } = useScroll();
    useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
                scrolled || open
                    ? "border-b border-ink/5 bg-white/80 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.08)] backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 md:px-12">
                <Logo />
                <div className="hidden items-center gap-8 lg:flex">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-${l.id}`}
                            onClick={() => go(l.id)}
                            className="group relative text-sm font-medium text-ink/70 transition-colors duration-300 hover:text-ink"
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-gold transition-[width] duration-300 group-hover:w-full" />
                        </button>
                    ))}
                    <button
                        data-testid="nav-quote-btn"
                        onClick={() => go("kontakt")}
                        className="rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-ink"
                    >
                        Umów wycenę
                    </button>
                </div>
                <button
                    data-testid="nav-mobile-toggle"
                    className="text-ink lg:hidden"
                    onClick={() => setOpen(!open)}
                    aria-label="Menu"
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </nav>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-6 pb-6">
                            {LINKS.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`nav-mobile-${l.id}`}
                                    onClick={() => go(l.id)}
                                    className="rounded-xl px-4 py-3 text-left font-display text-lg font-semibold text-ink transition-colors duration-200 hover:bg-mist"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                data-testid="nav-mobile-quote-btn"
                                onClick={() => go("kontakt")}
                                className="mt-2 rounded-full bg-gold px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-ink"
                            >
                                Umów wycenę
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
